import React, { useState, useCallback, useMemo } from 'react';
import { useMap } from '../../contexts/MapContext';
import { useCampaign } from '../../contexts/CampaignContext';
import { buildWallSegs, buildDoorVisionSegs, computeVisibleCells } from '../../lib/vision';
import TerrainPalette from './TerrainPalette';
import { TELEPORT_STYLES } from '../../lib/teleports';

// ── Tool definitions ──────────────────────────────────────────────────────
const WALL_STYLE_DEFS = {
  stone: { label: 'Stone', color: '#9a9ab8' },
  wood:  { label: 'Wood',  color: '#8b5e3c' },
  cave:  { label: 'Cave',  color: '#4a4a58' },
  brick: { label: 'Brick', color: '#8a4a38' },
  iron:  { label: 'Iron',  color: '#5a6070' },
};

const DOOR_STYLE_DEFS = {
  wood:  { label: 'Wood',  color: '#b87333' },
  iron:  { label: 'Iron',  color: '#6a7a8a' },
  stone: { label: 'Stone', color: '#8a8a9a' },
};
const DOOR_TYPE_DEFS = {
  door:   { label: '🚪 Door' },
  bars:   { label: '⛓ Bars' },
};
// 'secret' is still a valid door type but is placed from the Hidden section
// (so it lives alongside traps and stealthed tokens). Keep this map driving
// the Walls & Doors type buttons only.

const PATH_STYLE_DEFS = {
  dirt:    { label: 'Dirt',    color: '#8a6a3a' },
  stone:   { label: 'Stone',   color: '#8a8a8e' },
  grass:   { label: 'Grass',   color: '#4a7a30' },
  snow:    { label: 'Snow',    color: '#cfd8e6' },
  blood:   { label: 'Blood',   color: '#8a1a1a' },
  magical: { label: 'Magical', color: '#a070ff' },
};

const MIN_DIM = 10;
const MAX_DIM = 80;

// ── Collapsible section ───────────────────────────────────────────────────
function Section({ label, icon, open, onToggle, active, children }) {
  return (
    <div style={styles.section}>
      <button style={{ ...styles.sectionHeader, ...(active ? styles.sectionActive : {}) }} onClick={onToggle}>
        <span style={styles.sectionLeft}>
          <span style={styles.sectionIcon}>{icon}</span>
          <span style={styles.sectionLabel}>{label}</span>
        </span>
        <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={styles.sectionBody}>{children}</div>}
    </div>
  );
}

// ── Main toolbar (undo/redo + quick tools) ───────────────────────────────
function UndoRedo({ activeTool, onToolChange }) {
  const { undo, redo, canUndo, canRedo } = useMap();
  const isMeasure = activeTool === 'measure';
  return (
    <div style={styles.undoRow}>
      <button style={{ ...styles.undoBtn, ...(canUndo ? {} : styles.dimmed) }} onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)">
        ↩ Undo
      </button>
      <button style={{ ...styles.undoBtn, ...(canRedo ? {} : styles.dimmed) }} onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
        ↪ Redo
      </button>
      <button
        style={{ ...styles.undoBtn, ...(isMeasure ? styles.measureActive : {}) }}
        onClick={() => onToolChange(isMeasure ? null : 'measure')}
        title="Measure (M) — click & drag to read distance in feet"
      >
        📐 Measure
      </button>
    </div>
  );
}

// ── Terrain section ───────────────────────────────────────────────────────
function TerrainTools({ activeTool, onToolChange }) {
  return (
    <div style={styles.toolRow}>
      {[
        { id: 'paint',  label: '✏️ Paint',  title: 'Paint terrain' },
        { id: 'eraser', label: '⬜ Erase',  title: 'Eraser' },
        { id: 'fill',   label: '🪣 Fill',   title: 'Fill area' },
      ].map((t) => (
        <button
          key={t.id}
          style={{ ...styles.toolBtn, ...(activeTool === t.id ? styles.activeTool : {}) }}
          onClick={() => onToolChange(t.id)}
          title={t.title}
        >{t.label}</button>
      ))}
    </div>
  );
}

// ── Walls & Doors section ─────────────────────────────────────────────────
function WallDoorTools({ activeTool, onToolChange, activeWallStyle, onWallStyleChange, activeDoorStyle, onDoorStyleChange, activeDoorType, onDoorTypeChange, wallCurved, onWallCurvedChange }) {
  const { clearWalls, walls } = useMap();
  const isWallTool = activeTool === 'wall-pen' || activeTool === 'wall-eraser' || activeTool === 'wall-select';
  const isDoorTool = activeTool === 'door-pen' || activeTool === 'wall-to-door';

  return (
    <>
      <span style={styles.subLabel}>Walls</span>
      <div style={styles.toolRow}>
        {[
          { id: 'wall-pen',    label: '🖊 Draw',   title: 'Click to add points, double-click to finish. Click near start to close.' },
          { id: 'wall-eraser', label: '✂ Erase',   title: 'Click a wall to remove it' },
          { id: 'wall-select', label: '↖ Select',  title: 'Select wall, Delete to remove' },
        ].map((t) => (
          <button
            key={t.id}
            style={{ ...styles.toolBtn, ...(activeTool === t.id ? styles.activeWallTool : {}) }}
            onClick={() => onToolChange(t.id)}
            title={t.title}
          >{t.label}</button>
        ))}
      </div>

      {isWallTool && (
        <div style={styles.styleGrid}>
          {Object.entries(WALL_STYLE_DEFS).map(([id, def]) => (
            <button
              key={id}
              style={{
                ...styles.styleBtn,
                borderColor: activeWallStyle === id ? def.color : '#4a3d28',
                color:       activeWallStyle === id ? def.color : '#9a8b6e',
                background:  activeWallStyle === id ? `${def.color}18` : '#0e1528',
              }}
              onClick={() => onWallStyleChange(id)}
            >
              <span style={{ color: def.color }}>━</span> {def.label}
            </button>
          ))}
        </div>
      )}

      {isWallTool && (
        <button
          style={{ ...styles.toolBtn, width: '100%', marginTop: 6, ...(wallCurved ? styles.activeCurveTool : {}) }}
          onClick={() => onWallCurvedChange(!wallCurved)}
        >
          {wallCurved ? '〰 Curved' : '─ Straight'}
        </button>
      )}

      {walls.length > 0 && (
        <button
          style={styles.dangerBtn}
          onClick={() => { if (window.confirm(`Delete all ${walls.length} wall segment(s)?`)) clearWalls(); }}
        >
          🗑 Clear Walls ({walls.length})
        </button>
      )}

      <div style={styles.divider} />

      <span style={styles.subLabel}>Doors</span>
      <div style={styles.toolRow}>
        {[
          { id: 'door-pen',     label: '🚪 Place',   title: 'Click two points to place a door' },
          { id: 'wall-to-door', label: '↔ Convert',  title: 'Click a wall to convert to door' },
        ].map((t) => (
          <button
            key={t.id}
            style={{ ...styles.toolBtn, ...(activeTool === t.id ? styles.activeDoorTool : {}) }}
            onClick={() => onToolChange(t.id)}
            title={t.title}
          >{t.label}</button>
        ))}
      </div>

      {isDoorTool && (
        <>
          <div style={styles.styleGrid}>
            {Object.entries(DOOR_STYLE_DEFS).map(([id, def]) => (
              <button
                key={id}
                style={{
                  ...styles.styleBtn,
                  borderColor: activeDoorStyle === id ? def.color : '#4a3d28',
                  color:       activeDoorStyle === id ? def.color : '#9a8b6e',
                  background:  activeDoorStyle === id ? `${def.color}18` : '#0e1528',
                }}
                onClick={() => onDoorStyleChange(id)}
              >
                <span style={{ color: def.color }}>━</span> {def.label}
              </button>
            ))}
          </div>
          <div style={styles.styleGrid}>
            {Object.entries(DOOR_TYPE_DEFS).map(([id, def]) => (
              <button
                key={id}
                style={{
                  ...styles.styleBtn,
                  borderColor: activeDoorType === id ? '#b87333' : '#4a3d28',
                  color:       activeDoorType === id ? '#f0e6d0' : '#9a8b6e',
                  background:  activeDoorType === id ? '#b8733318' : '#0e1528',
                }}
                onClick={() => onDoorTypeChange(id)}
              >
                {def.label}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ── Paths section (decorative; no gameplay effect) ───────────────────────
function PathTools({ activeTool, onToolChange, activePathStyle, onPathStyleChange, pathCurved, onPathCurvedChange }) {
  const { clearPaths, paths } = useMap();
  const isPathTool = activeTool === 'path-pen' || activeTool === 'path-eraser' || activeTool === 'path-select';

  return (
    <>
      <span style={styles.subLabel}>Paths</span>
      <div style={styles.toolRow}>
        {[
          { id: 'path-pen',    label: '🖊 Draw',  title: 'Click to add points, double-click to finish' },
          { id: 'path-eraser', label: '✂ Erase',  title: 'Click a path segment to remove it' },
          { id: 'path-select', label: '↖ Select', title: 'Select path, drag to move, Delete to remove' },
        ].map((t) => (
          <button
            key={t.id}
            style={{ ...styles.toolBtn, ...(activeTool === t.id ? styles.activeWallTool : {}) }}
            onClick={() => onToolChange(t.id)}
            title={t.title}
          >{t.label}</button>
        ))}
      </div>

      {isPathTool && (
        <div style={styles.styleGrid}>
          {Object.entries(PATH_STYLE_DEFS).map(([id, def]) => (
            <button
              key={id}
              style={{
                ...styles.styleBtn,
                borderColor: activePathStyle === id ? def.color : '#4a3d28',
                color:       activePathStyle === id ? def.color : '#9a8b6e',
                background:  activePathStyle === id ? `${def.color}18` : '#0e1528',
              }}
              onClick={() => onPathStyleChange(id)}
            >
              <span style={{ color: def.color }}>━</span> {def.label}
            </button>
          ))}
        </div>
      )}

      {isPathTool && (
        <button
          style={{ ...styles.toolBtn, width: '100%', marginTop: 6, ...(pathCurved ? styles.activeCurveTool : {}) }}
          onClick={() => onPathCurvedChange(!pathCurved)}
        >
          {pathCurved ? '〰 Curved' : '─ Straight'}
        </button>
      )}

      {paths.length > 0 && (
        <button
          style={styles.dangerBtn}
          onClick={() => { if (window.confirm(`Delete all ${paths.length} path(s)?`)) clearPaths(); }}
        >
          🗑 Clear Paths ({paths.length})
        </button>
      )}

      <span style={styles.hint}>Decorative only — no effect on movement, vision, or fog.</span>
    </>
  );
}

// ── Fog section ───────────────────────────────────────────────────────────
function FogTools({ activeTool, onToolChange }) {
  const { revealAll, hideAll, paintFogCells, walls, doors, cells, gridW, gridH } = useMap();
  const { tokens } = useCampaign();

  const wallSegs       = useMemo(() => buildWallSegs(walls), [walls]);
  const doorVisionSegs = useMemo(() => buildDoorVisionSegs(doors, false), [doors]);
  const allSegs        = useMemo(() => [...wallSegs, ...doorVisionSegs], [wallSegs, doorVisionSegs]);

  const resetFog = useCallback(() => {
    hideAll();
    const allVisible = [];
    for (const token of Object.values(tokens)) {
      if (token.type !== 'player' && token.type !== 'friendlyNPC') continue;
      if (token.defeated) continue;
      const size = token.size || 1;
      const visible = computeVisibleCells(token.x, token.y, size, allSegs, cells, gridW, gridH, (token.visionRange || 60) / 5);
      allVisible.push(...visible);
    }
    if (allVisible.length > 0) paintFogCells(allVisible, true);
  }, [hideAll, paintFogCells, tokens, allSegs, cells, gridW, gridH]);

  return (
    <>
      <div style={styles.toolRow}>
        <button
          style={{ ...styles.toolBtn, ...(activeTool === 'fog-reveal' ? styles.activeFogTool : {}) }}
          onClick={() => onToolChange('fog-reveal')}
          title="Brush to reveal fog"
        >👁 Reveal</button>
        <button
          style={{ ...styles.toolBtn, ...(activeTool === 'fog-hide' ? styles.activeFogTool : {}) }}
          onClick={() => onToolChange('fog-hide')}
          title="Brush to hide fog"
        >🌑 Hide</button>
      </div>
      <div style={{ ...styles.toolRow, marginTop: 6 }}>
        <button className="btn-ghost btn-sm" style={{ flex: 1 }} onClick={revealAll}>Reveal All</button>
        <button className="btn-ghost btn-sm" style={{ flex: 1 }} onClick={hideAll}>Hide All</button>
        <button className="btn-ghost btn-sm" style={{ flex: 1 }} onClick={resetFog} title="Reset to player vision">Reset</button>
      </div>
    </>
  );
}

// ── Map Settings section ──────────────────────────────────────────────────
function MapSettings({ activeTool, onToolChange }) {
  const { gridW, gridH, resizeGrid, clearMap, spawnPoint } = useMap();
  const { tokens, removeToken } = useCampaign();
  const [inputW, setInputW] = useState('');
  const [inputH, setInputH] = useState('');
  const isSpawn = activeTool === 'spawn';

  const handleResize = () => {
    const newW = inputW === '' ? gridW : parseInt(inputW, 10);
    const newH = inputH === '' ? gridH : parseInt(inputH, 10);
    if (isNaN(newW) || isNaN(newH)) return;
    if (newW < MIN_DIM || newW > MAX_DIM || newH < MIN_DIM || newH > MAX_DIM) {
      alert(`Grid dimensions must be between ${MIN_DIM} and ${MAX_DIM}.`);
      return;
    }
    if (newW === gridW && newH === gridH) return;
    const shrinking = newW < gridW || newH < gridH;
    const msg = shrinking
      ? `Resize grid to ${newW}×${newH}?\n\nCells outside the new bounds will be removed.`
      : `Resize grid to ${newW}×${newH}?`;
    if (!window.confirm(msg)) return;
    resizeGrid(newW, newH);
    setInputW('');
    setInputH('');
  };

  return (
    <>
      {/* Spawn Zone */}
      <span style={styles.subLabel}>Spawn Zone</span>
      <button
        style={{ ...styles.toolBtn, width: '100%', ...(isSpawn ? styles.activeSpawnTool : {}) }}
        onClick={() => onToolChange(isSpawn ? null : 'spawn')}
      >
        {isSpawn ? '🏠 Click map to place…' : '🏠 Move Spawn Zone'}
      </button>
      {spawnPoint && <span style={styles.hint}>Position: ({spawnPoint.x}, {spawnPoint.y})</span>}

      <div style={styles.divider} />

      {/* Grid Size */}
      <span style={styles.subLabel}>Grid Size</span>
      <div style={styles.sizeRow}>
        <div style={styles.dimField}>
          <span style={styles.dimLabel}>W</span>
          <input style={styles.dimInput} type="number" min={MIN_DIM} max={MAX_DIM}
            value={inputW} placeholder={String(gridW)} onChange={(e) => setInputW(e.target.value)} />
        </div>
        <span style={styles.dimSep}>×</span>
        <div style={styles.dimField}>
          <span style={styles.dimLabel}>H</span>
          <input style={styles.dimInput} type="number" min={MIN_DIM} max={MAX_DIM}
            value={inputH} placeholder={String(gridH)} onChange={(e) => setInputH(e.target.value)} />
        </div>
        <button style={styles.resizeBtn} onClick={handleResize}>Resize</button>
      </div>
      <span style={styles.hint}>Current: {gridW}×{gridH}</span>

      <div style={styles.divider} />

      {/* Clear Map */}
      <button
        style={styles.dangerBtn}
        onClick={() => {
          if (window.confirm('Clear the entire map? All terrain, walls, doors, monsters, objects and NPCs will be erased. Player tokens are kept.')) {
            clearMap();
            Object.values(tokens).filter((t) => t.type !== 'player').forEach((t) => removeToken(t.id));
          }
        }}
      >
        🗑 New Map (Clear All)
      </button>
    </>
  );
}

// ── Main export: combined map editor ──────────────────────────────────────
// ── Elevation section ─────────────────────────────────────────────────────
function ElevationTools({ activeTool, onToolChange, activeElevation, onElevationChange }) {
  const isElev = activeTool === 'elevation';
  return (
    <>
      <button
        style={{ ...styles.toolBtn, width: '100%', ...(isElev ? styles.activeElevTool : {}) }}
        onClick={() => onToolChange(isElev ? null : 'elevation')}
      >
        {isElev ? '🏔 Click map to paint…' : '🏔 Paint Elevation'}
      </button>
      <div style={styles.elevRow}>
        <span style={styles.subLabel}>Level (×5 ft)</span>
        <div style={styles.elevStepper}>
          <button style={styles.elevStepBtn} onClick={() => onElevationChange(Math.max(0, activeElevation - 1))}>−</button>
          <span style={styles.elevValue}>{activeElevation}</span>
          <button style={styles.elevStepBtn} onClick={() => onElevationChange(Math.min(10, activeElevation + 1))}>+</button>
        </div>
      </div>
      <span style={styles.hint}>{activeElevation === 0 ? 'Ground level (0 ft)' : `${activeElevation * 5} ft high — costs extra movement to climb`}</span>
      {activeElevation >= 2 && <span style={{ ...styles.hint, color: '#e07070' }}>2+ level gap = cliff (blocks movement)</span>}
    </>
  );
}

// ── Lighting section ──────────────────────────────────────────────────────
// Full-stage multiply tint over the map. Cheap atmosphere — five presets +
// Off. Persisted on the map doc so all views sync.
const LIGHTING_PRESETS = [
  { id: null,      label: '☀ Off',     color: '#ffffff', alpha: 0 },
  { id: 'morning', label: '🌅 Morning', color: '#fde68a', alpha: 0.14 },
  { id: 'day',     label: '🌞 Day',     color: '#ffffff', alpha: 0 },
  { id: 'dusk',    label: '🌆 Dusk',    color: '#fb923c', alpha: 0.18 },
  { id: 'night',   label: '🌙 Night',   color: '#1e3a8a', alpha: 0.32 },
  { id: 'cave',    label: '🪨 Cave',    color: '#0f172a', alpha: 0.30 },
];

function LightingTools({ ambient, onAmbientChange }) {
  const current = ambient?.preset ?? null;
  return (
    <>
      <div style={styles.styleGrid}>
        {LIGHTING_PRESETS.map((p) => {
          const active = current === p.id;
          return (
            <button
              key={String(p.id)}
              style={{
                ...styles.styleBtn,
                borderColor: active ? '#fbbf24' : '#4a3d28',
                color:       active ? '#fde68a' : '#9a8b6e',
                background:  active ? 'rgba(251,191,36,0.16)' : '#0e1528',
              }}
              onClick={() => onAmbientChange(p.id === null ? null : { preset: p.id, color: p.color, alpha: p.alpha })}
            >{p.label}</button>
          );
        })}
      </div>
      <span style={styles.hint}>Multiply-blended tint across the whole map. Synced to every view.</span>
    </>
  );
}

function HiddenTools({ activeTool, onToolChange, activeDoorType, onDoorTypeChange }) {
  const isSecretDoorTool = activeTool === 'door-pen' && activeDoorType === 'secret';
  return (
    <div style={styles.toolRow}>
      <button
        style={{ ...styles.toolBtn, ...(activeTool === 'trap-place' ? styles.activeTool : {}) }}
        onClick={() => onToolChange(activeTool === 'trap-place' ? null : 'trap-place')}
        title="Click a cell to place a trap"
      >🪤 Trap</button>
      <button
        style={{ ...styles.toolBtn, ...(isSecretDoorTool ? styles.activeTool : {}) }}
        onClick={() => {
          if (isSecretDoorTool) {
            onToolChange(null);
            return;
          }
          // Two-step activation: set type='secret' then enter door-pen mode.
          onDoorTypeChange('secret');
          onToolChange('door-pen');
        }}
        title="Click two points to place a hidden door"
      >🔍 Secret Door</button>
    </div>
  );
}

function TeleportTools({
  activeTool, onToolChange,
  activeTeleportStyle, onTeleportStyleChange,
  teleportTwoWay, onTeleportTwoWayChange,
  teleportHidden, onTeleportHiddenChange,
  teleportDC,     onTeleportDCChange,
}) {
  const isTeleportTool = activeTool === 'teleport-pen';
  const isTrapdoor     = activeTeleportStyle === 'trapdoor';
  return (
    <>
      <button
        style={{ ...styles.toolBtn, width: '100%', ...(isTeleportTool ? styles.activeTool : {}) }}
        onClick={() => onToolChange(isTeleportTool ? null : 'teleport-pen')}
        title="Click two points to link a teleport (stairs / pit / portal / trapdoor)"
      >🌀 {isTeleportTool ? 'Click endpoint A…' : 'Place Teleport'}</button>

      <span style={{ ...styles.subLabel, marginTop: 8 }}>Style</span>
      <div style={styles.styleGrid}>
        {Object.entries(TELEPORT_STYLES).map(([id, def]) => (
          <button
            key={id}
            style={{
              ...styles.styleBtn,
              borderColor: activeTeleportStyle === id ? def.color : '#4a3d28',
              color:       activeTeleportStyle === id ? def.color : '#9a8b6e',
              background:  activeTeleportStyle === id ? `${def.color}22` : '#0e1528',
            }}
            onClick={() => onTeleportStyleChange(id)}
          >
            <span style={{ color: def.color }}>{def.icon}</span> {def.label}
          </button>
        ))}
      </div>

      <button
        style={{ ...styles.toolBtn, width: '100%', marginTop: 6, ...(teleportTwoWay ? styles.activeCurveTool : {}) }}
        onClick={() => onTeleportTwoWayChange(!teleportTwoWay)}
      >
        {teleportTwoWay ? '↔ Two-way' : '→ One-way (A to B)'}
      </button>

      {isTrapdoor && (
        <>
          <label style={{ ...styles.toggleLabel, marginTop: 6 }}>
            <input
              type="checkbox"
              checked={!!teleportHidden}
              onChange={(e) => onTeleportHiddenChange(e.target.checked)}
            />
            <span>Hidden (requires perception)</span>
          </label>
          {teleportHidden && (
            <div style={{ ...styles.fieldRow, marginTop: 4 }}>
              <span style={{ fontSize: 10, color: '#9a8b6e', fontWeight: 700, textTransform: 'uppercase' }}>DC</span>
              <input
                type="number"
                style={styles.dcInput}
                value={teleportDC}
                min={1}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  onTeleportDCChange(Number.isFinite(v) ? v : 12);
                }}
              />
            </div>
          )}
        </>
      )}

      <span style={styles.hint}>
        Click once to set endpoint A, then once more to link to endpoint B. ESC to cancel.
      </span>
    </>
  );
}

export default function EditorToolbar({
  activeTool, onToolChange, activeWallStyle, onWallStyleChange, activeDoorStyle, onDoorStyleChange, activeDoorType, onDoorTypeChange, activeTerrain, onTerrainSelect, activeElevation, onElevationChange, wallCurved, onWallCurvedChange, activePathStyle, onPathStyleChange, pathCurved, onPathCurvedChange,
  activeTeleportStyle, onTeleportStyleChange, teleportTwoWay, onTeleportTwoWayChange, teleportHidden, onTeleportHiddenChange, teleportDC, onTeleportDCChange,
}) {
  const isTerrain   = activeTool === 'paint' || activeTool === 'eraser' || activeTool === 'fill';
  const isWall      = activeTool === 'wall-pen' || activeTool === 'wall-eraser' || activeTool === 'wall-select';
  const isDoor      = activeTool === 'door-pen' || activeTool === 'wall-to-door';
  const isPath      = activeTool === 'path-pen' || activeTool === 'path-eraser' || activeTool === 'path-select';
  const isFog       = activeTool === 'fog-reveal' || activeTool === 'fog-hide';
  const isSpawn     = activeTool === 'spawn';
  const isElevation = activeTool === 'elevation';
  const isHidden    = activeTool === 'trap-place' || (activeTool === 'door-pen' && activeDoorType === 'secret');
  const isTeleport  = activeTool === 'teleport-pen';

  const { ambient, setAmbient } = useMap();
  const [openSections, setOpenSections] = useState({ terrain: true, walls: false, paths: false, fog: false, elevation: false, hidden: false, teleports: false, lighting: false, settings: false });
  const toggle = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const terrainOpen   = openSections.terrain;
  const wallsOpen     = openSections.walls     || isWall || isDoor;
  const pathsOpen     = openSections.paths     || isPath;
  const fogOpen       = openSections.fog       || isFog;
  const elevationOpen = openSections.elevation || isElevation;
  const hiddenOpen    = openSections.hidden    || isHidden;
  const teleportsOpen = openSections.teleports || isTeleport;
  const lightingOpen  = openSections.lighting;
  const settingsOpen  = openSections.settings  || isSpawn;

  return (
    <div style={styles.container}>
      <UndoRedo activeTool={activeTool} onToolChange={onToolChange} />
      <div style={styles.scrollArea}>
        <Section label="Terrain" icon="🎨" open={terrainOpen} onToggle={() => toggle('terrain')} active={isTerrain}>
          <TerrainTools activeTool={activeTool} onToolChange={onToolChange} />
          <TerrainPalette selectedTerrain={activeTerrain} onSelect={onTerrainSelect} />
        </Section>

        <Section label="Walls & Doors" icon="🧱" open={wallsOpen} onToggle={() => toggle('walls')} active={isWall || isDoor}>
          <WallDoorTools
            activeTool={activeTool} onToolChange={onToolChange}
            activeWallStyle={activeWallStyle} onWallStyleChange={onWallStyleChange}
            activeDoorStyle={activeDoorStyle} onDoorStyleChange={onDoorStyleChange}
            activeDoorType={activeDoorType} onDoorTypeChange={onDoorTypeChange}
            wallCurved={wallCurved} onWallCurvedChange={onWallCurvedChange}
          />
        </Section>

        <Section
          label="Paths"
          icon="🪧"
          open={pathsOpen}
          onToggle={() => {
            // While a path tool is active, `pathsOpen` is forced true via `|| isPath`,
            // so a plain toggle of openSections.paths can't actually close the section.
            // When the user collapses, also deactivate the path tool so the section
            // can fall out of the forced-open branch.
            if (pathsOpen && isPath) onToolChange(null);
            toggle('paths');
          }}
          active={isPath}
        >
          <PathTools
            activeTool={activeTool} onToolChange={onToolChange}
            activePathStyle={activePathStyle} onPathStyleChange={onPathStyleChange}
            pathCurved={pathCurved} onPathCurvedChange={onPathCurvedChange}
          />
        </Section>

        <Section label="Elevation" icon="🏔" open={elevationOpen} onToggle={() => toggle('elevation')} active={isElevation}>
          <ElevationTools activeTool={activeTool} onToolChange={onToolChange} activeElevation={activeElevation ?? 0} onElevationChange={onElevationChange} />
        </Section>

        <Section label="Fog of War" icon="🌫" open={fogOpen} onToggle={() => toggle('fog')} active={isFog}>
          <FogTools activeTool={activeTool} onToolChange={onToolChange} />
        </Section>

        <Section label="Lighting" icon="🌅" open={lightingOpen} onToggle={() => toggle('lighting')} active={!!ambient?.preset}>
          <LightingTools ambient={ambient} onAmbientChange={setAmbient} />
        </Section>

        <Section label="Hidden" icon="🕵" open={hiddenOpen} onToggle={() => toggle('hidden')} active={isHidden}>
          <HiddenTools
            activeTool={activeTool} onToolChange={onToolChange}
            activeDoorType={activeDoorType} onDoorTypeChange={onDoorTypeChange}
          />
        </Section>

        <Section label="Teleports" icon="🌀" open={teleportsOpen} onToggle={() => toggle('teleports')} active={isTeleport}>
          <TeleportTools
            activeTool={activeTool} onToolChange={onToolChange}
            activeTeleportStyle={activeTeleportStyle} onTeleportStyleChange={onTeleportStyleChange}
            teleportTwoWay={teleportTwoWay} onTeleportTwoWayChange={onTeleportTwoWayChange}
            teleportHidden={teleportHidden} onTeleportHiddenChange={onTeleportHiddenChange}
            teleportDC={teleportDC} onTeleportDCChange={onTeleportDCChange}
          />
        </Section>

        <Section label="Map Settings" icon="⚙" open={settingsOpen} onToggle={() => toggle('settings')} active={isSpawn}>
          <MapSettings activeTool={activeTool} onToolChange={onToolChange} />
        </Section>
      </div>
    </div>
  );
}

// ── Keep these exports for backward compat but they're now unused ─────────
export function FogQuickActions() { return null; }
export function SpawnZonePanel() { return null; }
export function GridSizePanel() { return null; }
export function WallStylePanel() { return null; }
export function DoorStylePanel() { return null; }

// ── Styles ────────────────────────────────────────────────────────────────
const styles = {
  container:      { display: 'flex', flexDirection: 'column', flexShrink: 0 },
  scrollArea:     { },

  // Undo/Redo bar
  undoRow:        { display: 'flex', gap: 4, padding: '6px 10px', borderBottom: '2px solid #8b7340', flexShrink: 0, background: '#0e1528' },
  undoBtn:        { flex: 1, height: 28, background: '#142038', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 0 },
  measureActive:  { borderColor: '#facc15', color: '#facc15', boxShadow: '0 0 6px rgba(250,204,21,0.4)' },

  // Sections
  section:        { borderBottom: '1px solid #4a3d28' },
  sectionHeader:  { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'transparent', border: 'none', borderRadius: 0, color: '#9a8b6e', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', letterSpacing: '0.04em' },
  sectionActive:  { color: '#d4a847', background: 'rgba(212,168,71,0.06)' },
  sectionLeft:    { display: 'flex', alignItems: 'center', gap: 8 },
  sectionIcon:    { fontSize: 14 },
  sectionLabel:   { fontSize: 12 },
  chevron:        { color: '#6a5d45', fontSize: 10 },
  sectionBody:    { padding: '4px 14px 12px' },

  // Tools
  toolRow:        { display: 'flex', gap: 4, marginBottom: 4 },
  toolBtn:        { flex: 1, height: 32, background: '#142038', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', fontSize: 11, fontWeight: 600, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '0 6px', transition: 'all 0.12s' },
  activeTool:     { background: '#1a2a48', borderColor: '#d4a847', color: '#d4a847', boxShadow: '0 0 8px rgba(212,168,71,0.3)' },
  activeWallTool: { background: '#1a2a48', borderColor: '#d4943a', color: '#d4943a', boxShadow: '0 0 8px rgba(212,148,58,0.3)' },
  activeDoorTool: { background: '#1a2a48', borderColor: '#d4a847', color: '#d4a847', boxShadow: '0 0 8px rgba(212,168,71,0.3)' },
  activeFogTool:  { background: '#0e1528', borderColor: '#7ab8e0', color: '#7ab8e0', boxShadow: '0 0 8px rgba(122,184,224,0.3)' },
  activeSpawnTool:{ background: '#0e1528', borderColor: '#5cb870', color: '#5cb870', boxShadow: '0 0 8px rgba(92,184,112,0.3)' },
  dimmed:         { opacity: 0.35, cursor: 'not-allowed' },

  // Subsections
  subLabel:       { fontSize: 10, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6a5d45', display: 'block', marginBottom: 5, marginTop: 2 },
  hint:           { fontSize: 10, color: '#6a5d45', display: 'block', marginTop: 4 },
  divider:        { height: 1, background: '#4a3d28', margin: '10px 0' },

  // Style pickers
  styleGrid:      { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, marginBottom: 4 },
  styleBtn:       { padding: '3px 8px', borderRadius: 3, border: '1px solid #4a3d28', fontSize: 11, fontWeight: 600, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.12s' },

  // Elevation
  activeCurveTool:{ background: '#0e1528', borderColor: '#c084fc', color: '#c084fc', boxShadow: '0 0 8px rgba(192,132,252,0.3)' },
  activeElevTool: { background: '#0e1528', borderColor: '#8bc34a', color: '#8bc34a', boxShadow: '0 0 8px rgba(139,195,74,0.3)' },
  elevRow:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 },
  elevStepper:    { display: 'flex', alignItems: 'center', gap: 6 },
  elevStepBtn:    { width: 28, height: 28, background: '#142038', border: '1px solid #4a3d28', borderRadius: 4, color: '#d4a847', fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  elevValue:      { fontSize: 16, fontWeight: 700, color: '#8bc34a', minWidth: 24, textAlign: 'center', fontFamily: "'Press Start 2P', monospace" },

  // Danger buttons
  dangerBtn:      { width: '100%', height: 28, marginTop: 8, background: 'transparent', border: '1px solid rgba(192,57,43,0.4)', borderRadius: 4, color: '#c0392b', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', letterSpacing: '0.03em' },

  // Grid size
  sizeRow:        { display: 'flex', alignItems: 'center', gap: 6 },
  dimField:       { display: 'flex', alignItems: 'center', gap: 4 },
  dimLabel:       { fontSize: 11, color: '#9a8b6e', fontWeight: 700, fontFamily: "'Press Start 2P', monospace" },
  dimInput:       { width: 44, height: 28, background: '#0a0f1e', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', fontSize: 12, textAlign: 'center', padding: '0 4px', outline: 'none' },
  dimSep:         { fontSize: 13, color: '#6a5d45' },
  resizeBtn:      { height: 28, padding: '0 10px', background: '#142038', border: '1px solid rgba(212,168,71,0.4)', borderRadius: 4, color: '#d4a847', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  toggleLabel:    { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#f0e6d0', cursor: 'pointer', marginTop: 6 },
  fieldRow:       { display: 'flex', alignItems: 'center', gap: 8 },
  dcInput:        { width: 60, height: 26, background: '#0a0f1e', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', fontSize: 12, textAlign: 'center', padding: '0 4px', outline: 'none' },
};
